"use client";
import type { MouseEventHandler, ReactNode } from "react";
import { AnchorScope, NESTED_LINK_ATTRIBUTE, useInsideAnchor } from "./AnchorNesting";

interface RichTextHyperlinkProps {
	uri: string;
	onClick: MouseEventHandler<HTMLAnchorElement>;
	children: ReactNode;
}

/**
 * The underline the anchor version gets from the browser's own link style
 * (this anchor carries no class of its own), kept on the span so the words
 * an author linked still read as linked inside the outer link.
 */
const NESTED_LINK_STYLE = { textDecoration: "underline" } as const;

/**
 * The inline hyperlink of a Studio rich text (Heading, BodyText, and any atom
 * rendering through makeDefaultRenderOptions). Outside an anchor it is the
 * plain <a> it always was; inside one (a Link Wrapper around the text) it is
 * a <span> carrying the link's text, underlined as the anchor was, so the
 * server never emits <a> inside <a> (lib/utils/studio/AnchorNesting.tsx). No
 * click handler on the span: the enclosing anchor navigates and reports the
 * click.
 */
export const RichTextHyperlink = ({ uri, onClick, children }: RichTextHyperlinkProps) => {
	const insideAnchor = useInsideAnchor();
	if (insideAnchor) {
		return (
			<span style={NESTED_LINK_STYLE} {...{ [NESTED_LINK_ATTRIBUTE]: uri }}>
				{children}
			</span>
		);
	}
	return (
		<a href={uri} onClick={onClick}>
			<AnchorScope>{children}</AnchorScope>
		</a>
	);
};
